<template>
  <DefaultLayout
    :pageTitle="meta.title"
    :pageDescription="meta.description"
    :showFooterCTA="true"
  >
    <PageHeader
      title="The Content Hub"
      subtitle-text="Everything we know about email, SMS and marketing automation — blog posts, guides, templates and case studies, all in one searchable place."
      class="mb-0 no-submenu"
    >
      <div class="hub__search-wrap">
        <svg class="hub__search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
        <input
          class="hub__search-input"
          type="text"
          v-model="searchQuery"
          placeholder="Search articles, guides, templates&hellip;"
        />
      </div>

      <div class="hub__tags">
        <span class="hub__tags-label">Popular:</span>
        <button
          type="button"
          class="hub__tag"
          v-for="topic in popularTopics"
          v-bind:key="topic"
          v-bind:class="{ 'hub__tag--active': activeTopics.includes(topic) }"
          v-on:click="toggleTopic(topic)"
        >
          {{ topic }}
        </button>
      </div>
    </PageHeader>

    <div class="container-fluid">
      <div class="row">
        <div class="col-xs-12">
          <div class="hub__layout">
            <div class="hub__sidebar">
              <div class="hub__sidebar-group">
                <p class="hub__sidebar-title">Content type</p>
                <label class="hub__check" v-for="type in contentTypes" v-bind:key="type">
                  <input type="checkbox" v-model="activeTypes" v-bind:value="type" />
                  <span class="hub__check-box"></span>
                  {{ type }}
                </label>
              </div>

              <div class="hub__sidebar-group">
                <p class="hub__sidebar-title">Topic</p>
                <label class="hub__check" v-for="topic in topics" v-bind:key="topic">
                  <input type="checkbox" v-model="activeTopics" v-bind:value="topic" />
                  <span class="hub__check-box"></span>
                  {{ topic }}
                </label>
              </div>

              <button
                type="button"
                class="hub__clear"
                v-if="activeTypes.length || activeTopics.length || searchQuery"
                v-on:click="clearFilters"
              >
                Clear all filters
              </button>
            </div>

            <div class="hub__main">
              <template v-if="!searchQuery && !activeTypes.length && !activeTopics.length">
                <p class="hub__section-label">Featured</p>
                <div class="hub__featured-grid">
                  <a
                    class="hub__card hub__card--featured"
                    href="#"
                    v-for="card in featuredCards"
                    v-bind:key="card.title"
                  >
                    <div class="hub__card-media hub__card-media--featured" v-bind:class="'hub__card-media--' + card.color">
                      <CardIllustration v-bind:type="card.type"></CardIllustration>
                    </div>
                    <div class="hub__card-body">
                      <span class="hub__card-type" v-bind:class="'hub__card-type--' + card.color">{{ card.type }}</span>
                      <p class="hub__card-title">{{ card.title }}</p>
                      <div class="hub__card-meta">{{ card.meta }}</div>
                    </div>
                  </a>
                </div>

                <div class="hub__section-head">
                  <p class="hub__section-label mb-0">Webinars</p>
                  <router-link class="hub__view-all" :to="{ name: 'Webinars' }">View all webinars &rarr;</router-link>
                </div>
                <div class="hub__grid hub__grid--webinars">
                  <a class="hub__card" href="#" v-for="card in webinarCards" v-bind:key="card.title">
                    <div class="hub__card-media" v-bind:class="'hub__card-media--' + card.color">
                      <CardIllustration v-bind:type="card.type"></CardIllustration>
                    </div>
                    <div class="hub__card-body">
                      <span class="hub__card-type" v-bind:class="'hub__card-type--' + card.color">{{ card.type }}</span>
                      <p class="hub__card-title">{{ card.title }}</p>
                      <div class="hub__card-meta">{{ card.meta }}</div>
                    </div>
                  </a>
                </div>
              </template>

              <p class="hub__section-label" v-bind:class="{ 'mt-10': !searchQuery && !activeTypes.length && !activeTopics.length }">
                {{ filteredCards.length }} result{{ filteredCards.length === 1 ? '' : 's' }}
              </p>

              <div class="hub__grid" v-if="filteredCards.length">
                <a class="hub__card" href="#" v-for="card in filteredCards" v-bind:key="card.title">
                  <div class="hub__card-media" v-bind:class="'hub__card-media--' + card.color">
                    <CardIllustration v-bind:type="card.type"></CardIllustration>
                  </div>
                  <div class="hub__card-body">
                    <span class="hub__card-type" v-bind:class="'hub__card-type--' + card.color">{{ card.type }}</span>
                    <p class="hub__card-title">{{ card.title }}</p>
                    <div class="hub__card-meta">{{ card.meta }}</div>
                  </div>
                </a>
              </div>

              <p class="hub__empty" v-else>
                No resources match those filters &mdash; try clearing one or two.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </DefaultLayout>
</template>

<script>
import DefaultLayout from "@/layouts/DefaultLayout";
import PageHeader from "@/components/Header/PageHeader";
import CardIllustration from "@/components/ContentHub/CardIllustration";

export default {
  name: "ContentHub",
  components: {
    DefaultLayout,
    PageHeader,
    CardIllustration,
  },
  data() {
    return {
      searchQuery: "",
      activeTypes: [],
      activeTopics: [],
      meta: {
        title: "Content Hub",
        description:
          "Everything we know about email, SMS and marketing automation — blog posts, guides, templates and case studies, all in one searchable place.",
      },
      contentTypes: ["Blog Article", "Guide", "Template", "Case Study", "Webinar", "eBook", "Glossary"],
      topics: ["Email Marketing", "Automation", "Deliverability", "SMS Marketing", "CRM & Integrations", "AI Marketing"],
      popularTopics: ["Deliverability", "Automation", "Email Marketing", "AI Marketing"],
      featuredCards: [
        { title: "Email Marketing: The Complete Guide", type: "Guide", topic: "Email Marketing", meta: "18 min read", color: "orange" },
        { title: "Scaling email for a growing ecommerce brand", type: "Case Study", topic: "Email Marketing", meta: "4 min read", color: "purple" },
        { title: "The SMB Marketing Blueprint", type: "eBook", topic: "Email Marketing", meta: "24 pages · PDF", color: "yellow" },
      ],
      cards: [
        { title: "Marketing Automation: The Complete Guide", type: "Blog Article", topic: "Automation", meta: "12 min read", color: "yellow" },
        { title: "Sender Reputation: What It Is & How to Protect It", type: "Guide", topic: "Deliverability", meta: "9 min read", color: "green" },
        { title: "A/B Testing for Email Marketing", type: "Blog Article", topic: "Email Marketing", meta: "7 min read", color: "blue" },
        { title: "Welcome email template pack", type: "Template", topic: "Email Marketing", meta: "5 templates", color: "pink" },
        { title: "Live: Getting started with marketing automation", type: "Webinar", topic: "Automation", meta: "On-demand · 42 min", color: "stone" },
        { title: "Customer Onboarding: Everything You Need to Know", type: "Blog Article", topic: "Automation", meta: "8 min read", color: "orange" },
        { title: "Spotlight: Deliverability terms explained", type: "Glossary", topic: "Deliverability", meta: "6 min read", color: "purple" },
        { title: "Email Segmentation: The Complete Guide", type: "Guide", topic: "Email Marketing", meta: "10 min read", color: "green" },
        { title: "Cutting support tickets with automated onboarding", type: "Case Study", topic: "Automation", meta: "3 min read", color: "pink" },
        { title: "Building high-converting email flows", type: "Webinar", topic: "Automation", meta: "Upcoming · Oct 14 · 45 min", color: "stone" },
        { title: "Deliverability deep dive: SPF, DKIM & DMARC", type: "Webinar", topic: "Deliverability", meta: "On-demand · 38 min", color: "stone" },
        { title: "The Ultimate Guide to Email Deliverability", type: "eBook", topic: "Deliverability", meta: "32 pages · PDF", color: "blue" },
        { title: "AI in Marketing: 2026 Playbook", type: "eBook", topic: "AI Marketing", meta: "28 pages · PDF", color: "blue" },
      ],
    };
  },
  computed: {
    webinarCards() {
      return this.cards.filter((card) => card.type === "Webinar");
    },
    filteredCards() {
      const query = this.searchQuery.trim().toLowerCase();
      const allCards = this.cards.concat(this.featuredCards);

      return allCards.filter((card) => {
        const matchesType = !this.activeTypes.length || this.activeTypes.includes(card.type);
        const matchesTopic = !this.activeTopics.length || this.activeTopics.includes(card.topic);
        const matchesQuery = !query || card.title.toLowerCase().includes(query);

        return matchesType && matchesTopic && matchesQuery;
      });
    },
  },
  methods: {
    toggleTopic(topic) {
      const index = this.activeTopics.indexOf(topic);
      if (index === -1) {
        this.activeTopics.push(topic);
      } else {
        this.activeTopics.splice(index, 1);
      }
    },
    clearFilters() {
      this.searchQuery = "";
      this.activeTypes = [];
      this.activeTopics = [];
    },
  },
};
</script>
